import { MediaImage, MediaVideo } from "@/components/admin/MediaAsset";
import { useRef, useState } from "react";
import { Video, X, Loader2, ImageIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MAX_BYTES = 100 * 1024 * 1024;
export const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${path}`;

export const VideoUploadButton = ({ onUploaded, disabled, onUploadingChange = () => {} }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [name, setName] = useState("");

  const upload = async (file) => {
    if (!file) return;
    if ((file.type && file.type !== "video/mp4") || !file.name.toLowerCase().endsWith(".mp4")) { toast.error("Format video harus MP4"); return; }
    if (file.size > MAX_BYTES) { toast.error(`Ukuran video ${(file.size / 1048576).toFixed(1)}MB — maksimal 100MB`); return; }
    setName(file.name);
    setProgress(0);
    onUploadingChange(true);
    try {
      const init = (await api.post("/upload/video/init", { filename: file.name, size: file.size, content_type: file.type })).data;
      const { upload_id, chunk_size, total_chunks } = init;
      for (let i = 0; i < total_chunks; i += 1) {
        const fd = new FormData();
        fd.append("upload_id", upload_id);
        fd.append("index", String(i));
        fd.append("chunk", file.slice(i * chunk_size, Math.min(file.size, (i + 1) * chunk_size)), "chunk");
        await api.post("/upload/video/chunk", fd, {
          onUploadProgress: (e) => setProgress(Math.min(99, Math.round(((i * chunk_size + (e.loaded || 0)) / file.size) * 100))),
        });
      }
      setProgress(99);
      const done = (await api.post("/upload/video/complete", { upload_id })).data;
      setProgress(100);
      onUploaded({ url: fileUrl(done.path), name: file.name, size: done.size });
      toast.success("Video berhasil diupload");
    } catch (e) {
      toast.error(`Upload video gagal: ${apiError(e)}`);
    } finally {
      setProgress(null);
      onUploadingChange(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="video/mp4,.mp4" className="hidden" data-testid="video-file-input" onChange={(e) => upload(e.target.files?.[0])} />
      <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={disabled || progress !== null} data-testid="upload-video-button" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-xs">
        {progress !== null ? <Loader2 size={14} className="animate-spin mr-1" /> : <Video size={14} className="mr-1" />} Upload Video
      </Button>
      {progress !== null && (
        <div className="w-full flex items-center gap-2" data-testid="video-upload-progress">
          <progress aria-label="Progres unggah video" value={progress} max={100} className="h-2 flex-1 accent-blue-500" />
          <span className="text-[11px] text-slate-600 font-mono-code w-10 text-right" data-testid="video-upload-percent">{progress}%</span>
          <span className="text-[11px] text-slate-500 truncate max-w-[160px]">{name}</span>
        </div>
      )}
    </>
  );
};

export const VideoPreview = ({ video, onRemove, cover, onPickCover, onPickFrame, onRemoveCover, extra, caption, onCaption, onGenCaption, genLoading }) => (
  <div className="mb-3 flex flex-wrap items-start gap-3" data-testid="video-preview">
    <div className="relative">
      <MediaVideo src={video.url} poster={cover || undefined} controls playsInline preload="metadata" className="w-44 max-h-72 object-contain rounded-xl border border-[#E2E8F0] bg-black" data-testid="video-preview-player" />
    </div>
    <div className="flex-1 min-w-[220px] space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-xs font-medium text-slate-800 truncate flex-1">{video.name}</p>
        <button type="button" onClick={onRemove} data-testid="video-remove-button" className="text-[11px] text-[#E11D48] hover:underline flex items-center gap-1 shrink-0"><X size={11} /> Hapus video</button>
      </div>
      <p className="text-[11px] text-slate-500">{(video.size / 1048576).toFixed(1)}MB · Instagram diposting sebagai Reels, Facebook sebagai video Page.</p>
      <div className="flex items-center gap-2 bg-[#FFFFFF] border border-[#E2E8F0] rounded-xl p-2" data-testid="video-cover-section">
        {cover ? (
          <MediaImage src={cover} alt="cover" className="w-12 h-16 rounded-lg object-cover border border-[#E2E8F0]" data-testid="video-cover-thumb" />
        ) : (
          <div className="w-12 h-16 rounded-lg border border-dashed border-[#CBD5E1] flex items-center justify-center"><ImageIcon size={14} className="text-slate-600" /></div>
        )}
        <div className="flex-1">
          <p className="text-[11px] font-medium text-slate-700">Cover / Thumbnail</p>
          <p className="text-[10px] text-slate-500">{cover ? "Cover dipakai untuk Reels & video Facebook" : "Opsional — tanpa cover, Meta memakai frame pertama"}</p>
        </div>
        <div className="flex flex-col gap-1">
          <Button type="button" size="sm" variant="outline" onClick={onPickFrame} data-testid="video-cover-frame-button" className="h-7 rounded-lg text-[11px]">Dari Frame Video</Button>
          <Button type="button" size="sm" variant="outline" onClick={onPickCover} data-testid="video-cover-pick-button" className="h-7 rounded-lg text-[11px]">{cover ? "Ganti dari Galeri" : "Dari Galeri"}</Button>
        </div>
        {cover && <button type="button" onClick={onRemoveCover} data-testid="video-cover-remove" className="text-slate-600 hover:text-[#E11D48]"><X size={14} /></button>}
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] font-medium text-slate-700">Caption video</p>
          <Button type="button" size="sm" variant="outline" onClick={onGenCaption} disabled={genLoading} data-testid="reels-caption-ai-button" className="h-7 rounded-lg text-[11px] border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#7C3AED] hover:bg-[#7C3AED]/20">
            {genLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />} Caption Reels AI
          </Button>
        </div>
        <Textarea value={caption} onChange={(e) => onCaption(e.target.value)} rows={5} maxLength={2200} placeholder="Kosongkan untuk memakai teks Instagram/Facebook hasil generate, atau klik Caption Reels AI" data-testid="video-caption-input" className="bg-[#F1F5F9] border-[#E2E8F0] text-xs" />
        <p className="text-[10px] text-slate-500 text-right" data-testid="video-caption-count">{caption.length}/2200</p>
      </div>
      {extra}
    </div>
  </div>
);
